import { useState, useEffect, useRef } from "react";
import { useScrollAnimation } from "@/hooks/useScrollAnimation";

function AnimatedNumber({ target, duration = 2500 }) {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const started = useRef(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started.current) {
          started.current = true;
          const start = performance.now();
          const step = (now) => {
            const elapsed = now - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setCount(Math.floor(eased * target));
            if (progress < 1) requestAnimationFrame(step);
          };
          requestAnimationFrame(step);
          obs.disconnect();
        }
      },
      { threshold: 0.3 }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [target, duration]);

  return (
    <span ref={ref} className="tabular-nums">
      {count.toLocaleString("de-DE")}
    </span>
  );
}

function RetroTV({ target, year, delay = 0 }) {
  return (
    <div className="inline-block" style={{ animationDelay: `${delay}ms` }}>
      <div className="relative rounded-[28px] p-3 sm:p-4 shadow-2xl" style={{ background: "linear-gradient(145deg, #3a3a3a, #1a1a1a)" }}>
        <div className="absolute -top-1 left-6 right-6 h-2 rounded-t-full" style={{ background: "linear-gradient(90deg, #5c3d2e, #8b6914, #5c3d2e)" }} />
        <div className="absolute -top-10 left-1/2 -translate-x-1/2 flex items-end gap-0">
          <div className="w-[2px] h-10 origin-bottom -rotate-[25deg]" style={{ background: "linear-gradient(to top, #555, #999)" }} />
          <div className="w-[2px] h-10 origin-bottom rotate-[25deg]" style={{ background: "linear-gradient(to top, #555, #999)" }} />
        </div>
        <div className="relative rounded-[20px] overflow-hidden" style={{ background: "#111" }}>
          <div
            className="relative flex flex-col items-center justify-center py-6 sm:py-8 px-8 sm:px-12"
            style={{
              background: "radial-gradient(ellipse at 50% 50%, #7DD4B5, #5ab895 50%, #3a9a75 100%)",
              boxShadow: "inset 0 0 60px rgba(0,0,0,0.3), inset 0 0 20px rgba(0,0,0,0.2)",
            }}
          >
            <div className="absolute inset-0 pointer-events-none" style={{ background: "radial-gradient(ellipse at 50% 50%, transparent 60%, rgba(0,0,0,0.25) 100%)" }} />
            <div className="absolute inset-0 pointer-events-none opacity-[0.06]" style={{ backgroundImage: "repeating-linear-gradient(0deg, transparent, transparent 2px, rgba(0,0,0,0.8) 2px, rgba(0,0,0,0.8) 4px)" }} />
            <div className="absolute inset-0 pointer-events-none animate-tv-flicker" />
            <span className="font-heading text-lg sm:text-xl font-bold tracking-tight relative z-10" style={{ color: "#1a3a2a", textShadow: "0 0 8px rgba(125,212,181,0.4)" }}>
              Runden {year}:
            </span>
            <span className="font-heading text-4xl sm:text-5xl lg:text-6xl font-black leading-none mt-1 relative z-10" style={{ color: "#1a3a2a", textShadow: "0 0 12px rgba(125,212,181,0.5)" }}>
              <AnimatedNumber target={target} duration={2800} />
            </span>
          </div>
        </div>
        <div className="flex items-center justify-center gap-3 mt-2 pb-1">
          <div className="w-3 h-3 rounded-full border border-gray-600" style={{ background: "linear-gradient(145deg, #666, #333)" }} />
          <div className="w-3 h-3 rounded-full border border-gray-600" style={{ background: "linear-gradient(145deg, #666, #333)" }} />
          <div className="w-8 h-1 rounded-full bg-gray-700 mx-1" />
          <div className="w-3 h-3 rounded-full border border-gray-600" style={{ background: "linear-gradient(145deg, #8b6914, #5c3d2e)" }} />
        </div>
      </div>
      <div className="flex justify-center gap-24 -mt-1">
        <div className="w-1 h-4 rounded-b" style={{ background: "#333" }} />
        <div className="w-1 h-4 rounded-b" style={{ background: "#333" }} />
      </div>
    </div>
  );
}

export default function RundenCounter() {
  const [sectionRef, visible] = useScrollAnimation({ threshold: 0.2 });

  return (
    <section
      ref={sectionRef}
      data-testid="runden-counter-section"
      className={`py-14 md:py-20 relative overflow-hidden animate-fade-up ${visible ? "visible" : ""}`}
      style={{ background: "#b46eb6" }}
    >
      <div className="absolute top-0 left-1/4 w-96 h-96 rounded-full opacity-15" style={{ background: "#7DD4B5", filter: "blur(120px)" }} />

      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 text-center relative z-10">
        <div className="flex flex-col sm:flex-row items-center justify-center gap-8 sm:gap-12 mb-8 pt-6">
          <RetroTV target={5180} year="2025" />
          <RetroTV target={5375} year="2026" delay={300} />
        </div>

        <h2 className="font-heading text-2xl sm:text-3xl md:text-4xl font-extrabold text-white mb-3 leading-tight">
          10.555 Runden in 2 Jahren!
        </h2>
        <p className="text-sm md:text-base leading-relaxed max-w-2xl mx-auto mb-6" style={{ color: "rgba(255,255,255,0.85)" }}>
          <strong className="text-white">5.180 Runden in 2025</strong> und unglaubliche <strong className="text-white">5.375 Runden in 2026</strong> – gemeinsam haben wir Geschichte geschrieben!
        </p>
        <a href="#spenden">
          <button
            data-testid="runden-cta-btn"
            className="rounded-full px-7 py-3 text-sm font-bold shadow-lg hover:shadow-xl transition-all hover:-translate-y-1 bg-white"
            style={{ color: "#b46eb6" }}
          >
            Jetzt die Forschung unterstützen
          </button>
        </a>
      </div>

      <style>{`
        @keyframes tvFlicker {
          0%, 100% { opacity: 0; }
          3% { opacity: 0.03; }
          6% { opacity: 0; }
          42% { opacity: 0; }
          44% { opacity: 0.02; }
          46% { opacity: 0; }
        }
        .animate-tv-flicker {
          animation: tvFlicker 4s infinite;
          background: rgba(255,255,255,1);
        }
      `}</style>
    </section>
  );
}
